import { useEffect, useMemo, useRef, useState } from 'react'

import history from '../../utils/history'
import { useQuery } from '../../hooks/useQuery'
import TextLine from '../atoms/TextLine'
import TeamChart from '../organisms/TeamChart'
import { Employee, EmployeeService, EmployeeSort } from '../../services/EmployeeService'
import { TableIcon } from '../atoms/Icons/TableIcon'
import { useSelector } from 'react-redux'
import { RootState } from '../../index'
import { normalizeText } from '../../utils/text'
import EmployeeAdminTable from '../organisms/EmployeeAdminTable'
import { SortOrderEnum } from '../../types/enums/sort-order.enum'
import Spinner from '../atoms/Spinner'
import { AppButton } from '../atoms/AppButton/AppButton'
import Breadcrumb, { BreadcrumbItem } from '../atoms/Breadcrumb/Breadcrumb'
import { TabFilter, Filter } from '../organisms/TabFilter'
import classNames from 'classnames'
import { NetworkConstants } from '../../utils/NetworkConstants'
import { createPathWithLang } from '../../utils/languagePathUtils'
import { PlusIcon } from '../atoms/Icons/PlusIcon'
import { trackEvent, TrackingCategoryEnum, TrackingCategoryEventEnum } from '../../services/EventTrackingService'
import { SectionType } from './ReviewsPage'
import UploadIcon from '../atoms/Icons/UploadIcon'
import toast, { ToastNotification, createToastBody } from '../molecules/ToastNotification'
import { RosterService, RosterImportStatus } from '../../services/RosterService'
import RosterImportModal from '../organisms/RosterImportModal'

const AdminPanelPage = (): JSX.Element => {
  const user = useSelector<RootState, Employee>(state => state.auth.user as Employee)
  const query = useQuery()
  const [breadcrumbData, setBreadcrumbData] = useState<BreadcrumbItem[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [searchInput, setSearchInput] = useState<string>(query.get('keyword') || '')
  const [searchIcon, setSearchIcon] = useState<boolean>(true)
  const [loadingTableData, setLoadingTableData] = useState<boolean>(false)
  const [activeSection, setActiveSection] = useState<SectionType>(query.get('view') === 'diagram' ? 'diagram' : 'table')
  const [uploadingRoster, setUploadingRoster] = useState<boolean>(false)
  const [rosterImportModalVisible, setRosterImportModalVisible] = useState<boolean>(false)
  const [rosterImportHash, setRosterImportHash] = useState<string>('')
  const [rosterFileName, setRosterFileName] = useState<string>('')
  const [selectedSort] = useState<EmployeeSort[]>([{ property: 'name', order: SortOrderEnum.Ascending }])
  const hiddenImportInput = useRef<HTMLInputElement>(null)

  // Creating an inside feature flag for toggling 'My Team' Filter tab
  const showDirectTab = false
  const filters = [new Filter('all', 'All'), ...(showDirectTab ? [new Filter('direct', 'My Team')] : [])]

  const [enabledFilter, setEnabledFilter] = useState<Filter>(filters[0])
  const handleFilterSelection = (selectedFilter: Filter): void => {
    if (selectedFilter.key !== 'all') {
      setSearchInput('')
    }
    setEnabledFilter(selectedFilter)
  }

  const employeesToShow = useMemo(() => {
    let fEmployees = [...employees]
    if (enabledFilter.key !== 'all') {
      // Logic for 'My Team' if it were enabled
    }

    if (searchInput) {
      const normalizedSearchText = normalizeText(searchInput)
      fEmployees = fEmployees.filter(({ name, email }) => {
        const inName = normalizeText(name).includes(normalizedSearchText)
        const inMail = normalizeText(email).includes(normalizedSearchText)
        return inName || inMail
      })
    }

    return EmployeeService.sortEmployees(fEmployees, selectedSort)
  }, [employees, searchInput, enabledFilter, selectedSort])

  useEffect(() => {
    trackEvent(TrackingCategoryEnum.ADMIN, TrackingCategoryEventEnum.ADMIN.PAGE_ON_LOAD)
    if (enabledFilter.key === 'all') {
      loadAllEmployees()
    } else {
      loadMyEmployees()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledFilter])

  useEffect(() => {
    generateBreadcrumb()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledFilter])

  const generateBreadcrumb = (): void => {
    const newBreadcrumbData = []
    let currentLabel = 'All'
    if (enabledFilter && enabledFilter.key !== 'all') {
      currentLabel = enabledFilter.label as string
    }
    newBreadcrumbData.push(
      searchInput && searchInput.length > 0
        ? { label: `Search (${employeesToShow.length} results)` }
        : { label: `${currentLabel} (${employeesToShow.length} results)` }
    )
    setBreadcrumbData(newBreadcrumbData)
  }

  useEffect(() => {
    generateBreadcrumb()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeesToShow])

  const loadEmployees = async (employeesLoadFunction: () => Promise<Employee[]>): Promise<void> => {
    setLoadingTableData(true)
    try {
      const response = await employeesLoadFunction()
      setEmployees(response)
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingTableData(false)
    }
  }

  const loadAllEmployees = async (): Promise<void> => {
    return loadEmployees(() => EmployeeService.findAllWithAccess(100))
  }

  const loadMyEmployees = async (): Promise<void> => {
    return loadEmployees(() => EmployeeService.findAllWithAccess(0))
  }

  const navigateToSection = (section: SectionType): void => {
    if (!section) return

    setActiveSection(section)
    query.set('view', section)
    history.replace({
      pathname: history.location.pathname,
      search: query.toString(),
    })
  }

  const handleEmployeeEdited = async (editedEmployee: Employee): Promise<void> => {
    if (editedEmployee) {
      await EmployeeService.update(editedEmployee.id, {
        email: editedEmployee.email,
        jobTitle: editedEmployee.jobTitle,
        name: editedEmployee.name,
        role: editedEmployee.role,
        salaryGrade: editedEmployee.salaryGrade,
        teams: editedEmployee.teams?.map(team => team.id) || [],
      })
      const updatedEmployeeIdx = employees.findIndex(e => e.id === editedEmployee.id)
      if (updatedEmployeeIdx !== undefined) {
        employees[updatedEmployeeIdx] = editedEmployee
        setEmployees([...employees])
      }
    }
  }

  const handleEmployeeDeleted = async (employeeId: number): Promise<void> => {
    try {
      await EmployeeService.delete(employeeId)
      setEmployees(employees.filter(e => e.id !== employeeId))
    } catch (e) {
      console.error(e)
    }
  }
  const handleSearchIcon = (value: string): void => {
    setSearchIcon(!value.length)
  }

  const handleClear = (): void => {
    setSearchInput('')
  }

  const handleTable = () => {
    trackEvent(TrackingCategoryEnum.ADMIN, TrackingCategoryEventEnum.ADMIN.CLICK_TABLE_BTN)
    navigateToSection('table')
  }

  const handleNew = () => {
    trackEvent(TrackingCategoryEnum.ADMIN, TrackingCategoryEventEnum.ADMIN.CLICK_NEW_BTN)
    history.push(createPathWithLang(NetworkConstants.URL_CREATE_ACCOUNT))
  }

  const handleRosterImport = async () => {
    trackEvent(TrackingCategoryEnum.ADMIN, TrackingCategoryEventEnum.ADMIN.CLICK_ROSTER_IMPORT_BTN)

    const rosterFile = hiddenImportInput.current?.files?.[0]

    if (!rosterFile) {
      toast.error(createToastBody({ title: 'Error', description: 'No file was selected.' }))
      return
    }

    try {
      setUploadingRoster(true)
      const uploadingToast = toast.loading('Uploading Roster file...')

      // Upload the roster file - server returns hash and status
      const importResponse = await RosterService.importRoster(rosterFile)
      toast.dismiss(uploadingToast)

      // Check if already completed on first try
      if (importResponse.status === 'ALREADY_COMPLETED' || importResponse.status === 'COMPLETED') {
        toast.success(
          createToastBody({
            title: importResponse.status === 'ALREADY_COMPLETED' ? 'Roster Already Imported' : 'Roster Import Complete',
            description:
              importResponse.status === 'ALREADY_COMPLETED'
                ? 'This file has already been imported successfully.'
                : 'The roster has been imported successfully.',
          })
        )
        setUploadingRoster(false)
        loadAllEmployees()
        return
      }

      if (importResponse.status === 'FAILED') {
        toast.error(
          createToastBody({
            title: 'Roster Import Failed',
            description: importResponse.errorMessage || 'Failed to import the roster file.',
          })
        )
        setUploadingRoster(false)
        return
      }

      // For COMPLETED or IN_PROGRESS, show the progress modal
      setRosterImportHash(importResponse.rosterHash)
      setRosterFileName(rosterFile.name)
      setRosterImportModalVisible(true)
    } catch (error) {
      console.error('Failed to upload roster file:', error)
      toast.error(
        createToastBody({
          title: 'Upload Error',
          description: 'Failed to upload the roster file. Please try again.',
        })
      )
      setUploadingRoster(false)
    }
  }

  const handleRosterImportComplete = (status: RosterImportStatus) => {
    setRosterImportModalVisible(false)
    setUploadingRoster(false)

    if (status.status === 'COMPLETED') {
      toast.success(
        createToastBody({
          title: 'Roster Import Complete',
          description: `Successfully imported ${status.totalEntries} employees`,
        })
      )
      // Reload employees after successful import
      loadAllEmployees()
    } else if (status.status === 'FAILED') {
      toast.error(
        createToastBody({
          title: 'Roster Import Failed',
          description: status.errorMessage || 'The import process failed. Please check the file and try again.',
        })
      )
    }
  }

  const handleRosterImportModalClose = () => {
    setRosterImportModalVisible(false)
    setUploadingRoster(false)
  }

  if (!user) {
    return <Spinner />
  }

  return (
    <div className="px-4 flex-grow flex flex-col">
      <ToastNotification />
      <RosterImportModal
        visible={rosterImportModalVisible}
        rosterHash={rosterImportHash}
        fileName={rosterFileName}
        onComplete={handleRosterImportComplete}
        onClose={handleRosterImportModalClose}
      />
      <div className="container mt-10 flex flex-wrap gap-4 md:gap-0">
        <h1 id="title" className="lg:absolute text-6xl text-dark-blue font-semibold">
          Manage users
        </h1>
        <div className="flex-1 flex justify-end lg:justify-center lg:mx-auto">
          <TextLine
            className="text-border border-orange chooseInput"
            type="text"
            value={searchInput}
            searchIcon={searchIcon}
            onChange={v => {
              setSearchInput(v)
              handleSearchIcon(v)
            }}
            onClear={handleClear}
            placeholder={'Quick find any team member'}
          />
        </div>
      </div>
      <div className="container mt-10 mb-4 relative flex flex-col gap-10">
        <div className={classNames({ flex: true, 'flex justify-end': activeSection === 'diagram' })}>
          <TabFilter
            filters={filters}
            onActiveFilterChange={handleFilterSelection}
            enabledFilter={enabledFilter}
          ></TabFilter>
          <div className="flex justify-end admin-button-container gap-4">
            {
              <>
                {activeSection === 'diagram' ? (
                  <AppButton type="clear" onClick={handleTable} className="gap-3">
                    <TableIcon />
                    <span>Table</span>
                  </AppButton>
                ) : null}
              </>
            }
            <AppButton type="clear" onClick={handleNew} className="gap-3">
              <PlusIcon color="#000000" />
              <span>New</span>
            </AppButton>
            <>
              <AppButton
                type="info"
                onClick={() => {
                  hiddenImportInput.current?.click()
                }}
                className="gap-3"
                disabled={uploadingRoster}
              >
                <UploadIcon />
                <span>{uploadingRoster ? 'Uploading...' : 'Upload Roster'}</span>
              </AppButton>
              <input
                type="file"
                ref={hiddenImportInput}
                className="hidden"
                accept=".csv"
                onChange={handleRosterImport}
              />
            </>
          </div>
        </div>
        {activeSection === 'table' && breadcrumbData.length && !loadingTableData ? (
          <Breadcrumb items={breadcrumbData} />
        ) : null}
      </div>
      {loadingTableData ? (
        <Spinner />
      ) : (
        <div className="container flex-grow mx-auto">
          {activeSection === 'table' ? (
            <>
              <EmployeeAdminTable
                employees={employeesToShow}
                onEdit={handleEmployeeEdited}
                onDelete={handleEmployeeDeleted}
              />
            </>
          ) : (
            <>
              <TeamChart
                employees={employees}
                highlight={searchInput ? employeesToShow.map(employee => employee.id) : undefined}
                currentUser={user}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default AdminPanelPage
